import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import FormLabel from "@mui/material/FormLabel";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import MuiCard from "@mui/material/Card";
import { styled } from "@mui/material/styles";
import ForgotPassword from "./components/ForgotPassword";
import AppTheme from "../shared-theme/AppTheme";
import ColorModeSelect from "../shared-theme/ColorModeSelect";
import { loginUser } from "../utility/api"; // Import the API utility
import { setUser } from "../features/UserSlice"; // Import Redux action
import logo from "../Malayan.png";
import backgroundImage from "../background.jpeg";
// import { AppBar, Toolbar, Avatar } from "@mui/material";
import Snackbar from '@mui/material/Snackbar';

const Card = styled(MuiCard)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	alignSelf: "center",
	width: "100%",
	padding: theme.spacing(4),
	gap: theme.spacing(2),
	margin: "auto",
	[theme.breakpoints.up("sm")]: {
		maxWidth: "450px",
	},
	boxShadow:
		"hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px",
	...theme.applyStyles("dark", {
		boxShadow:
			"hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px",
	}),
}));

const SignInContainer = styled(Stack)(({ theme }) => ({
	height: "calc((1 - var(--template-frame-height, 0)) * 100dvh)",
	minHeight: "100%",
	padding: theme.spacing(2),
	[theme.breakpoints.up("sm")]: {
		padding: theme.spacing(4),
	},
	"&::before": {
		content: '""',
		display: "block",
		position: "absolute",
		zIndex: -1,
		inset: 0,
		backgroundImage:
			"radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))",
		backgroundRepeat: "no-repeat",
		...theme.applyStyles("dark", {
			backgroundImage:
				"radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))",
		}),
	},
}));

export default function SignIn(props) {
	const navigate = useNavigate();
	const dispatch = useDispatch(); // Access Redux dispatch
	const [emailError, setEmailError] = React.useState(false);
	const [emailErrorMessage, setEmailErrorMessage] = React.useState("");
	const [passwordError, setPasswordError] = React.useState(false);
	const [passwordErrorMessage, setPasswordErrorMessage] = React.useState("");
	const [error, setError] = React.useState("");
	const [open, setOpen] = React.useState(false);

	// const handleClickOpen = () => {
	//   setOpen(true);
	// };

	const handleClose = () => {
		setOpen(false);
	};

	const handleSubmit = async (event) => {
		event.preventDefault(); // Prevent default form submission

		if (!validateInputs()) {
			return;
		}

		const data = new FormData(event.currentTarget);
		try {
			const user = await loginUser(
				data.get("email"),
				data.get("password")
			);
			if (user) {
				dispatch(setUser(user));
				if (user.role === "SysAdmin") {
					navigate("/createuser");
				} else if (user.defaultPasswodChanged) {
					navigate("/home");
				} else {
					navigate("/changePassword");
				}
			} else {
				throw new Error("User not found in response.");
			}
		} catch (error) {
			setError(error.message || "Login failed!");
		}
	};

	const validateInputs = () => {
		const email = document.getElementById("email");
		const password = document.getElementById("password");

		let isValid = true;

		if (!email.value || !/\S+@\S+\.\S+/.test(email.value)) {
			setEmailError(true);
			setEmailErrorMessage("Please enter a valid email address.");
			isValid = false;
		} else {
			setEmailError(false);
			setEmailErrorMessage("");
		}

		if (!password.value || password.value.length < 6) {
			setPasswordError(true);
			setPasswordErrorMessage(
				"Password must be at least 6 characters long."
			);
			isValid = false;
		} else {
			setPasswordError(false);
			setPasswordErrorMessage("");
		}

		return isValid;
	};

	return (
		<Box
			sx={{
				width: "100%",
				margin: "auto",
				backgroundImage: `url(${backgroundImage})`,
				backgroundSize: "cover",
				backgroundPosition: "center",
				backgroundRepeat: "no-repeat",
			}}
		>
			{/* <AppBar position="static">
				<Toolbar>
					<Avatar src={logo} />
					<Typography
						variant="h6"
						sx={{ flexGrow: 1, alignItems: "left" }}
					>
						
					</Typography>
				</Toolbar>
			</AppBar> */}
			<AppTheme {...props}>
			<div>
			{error && (<Snackbar
				open={open}
				autoHideDuration={5000}
				onClose={handleClose}
				message="Login failed!"
			/>)}
			</div>
				<CssBaseline enableColorScheme />
				<SignInContainer
					direction="column"
					justifyContent="space-between"
				>
					<ColorModeSelect
						sx={{ position: "fixed", top: "1rem", right: "1rem" }}
					/>
					<Card variant="outlined">
						<img
							src={logo}
							alt="Logo"
							style={{
								width: "150px",
								margin: "auto",
								marginBottom: "20px",
							}}
						/>
						<Typography
							component="h3"
							variant="h6"
							sx={{
								width: "100%",
								fontSize: "clamp(1rem, 5vw, 1.15rem)",
							}}
						>
							Electronic Malayan Adjustment System
						</Typography>
						<Box
							component="form"
							onSubmit={handleSubmit}
							noValidate
							sx={{
								display: "flex",
								flexDirection: "column",
								width: "100%",
								gap: 2,
							}}
						>
							<FormControl>
								<FormLabel htmlFor="email">Email</FormLabel>
								<TextField
									error={emailError}
									helperText={emailErrorMessage}
									id="email"
									type="email"
									name="email"
									placeholder="username"
									autoComplete="email"
									autoFocus
									required
									fullWidth
									variant="outlined"
									color={emailError ? "error" : "primary"}
								/>
							</FormControl>
							<FormControl>
								<FormLabel htmlFor="password">
									Password
								</FormLabel>
								<TextField
									error={passwordError}
									helperText={passwordErrorMessage}
									name="password"
									placeholder="•••••"
									type="password"
									id="password"
									autoComplete="current-password"
									autoFocus
									required
									fullWidth
									variant="outlined"
									color={passwordError ? "error" : "primary"}
								/>
							</FormControl>
							{/* <FormControlLabel
              control={<Checkbox value="remember" color="primary" />}
              label="Remember me"
            /> */}
							<ForgotPassword
								open={open}
								handleClose={handleClose}
							/>
							<Button type="submit" fullWidth variant="contained">
								Sign in
							</Button>
							{/* <Link
              component="button"
              type="button"
              onClick={handleClickOpen}
              variant="body2"
              sx={{ alignSelf: 'center' }}
            >
              Forgot your password?
            </Link> */}
						</Box>
					</Card>
				</SignInContainer>
			</AppTheme>
		</Box>
	);
}
